import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { UseUserCreate } from '@openx/components-administration/lib/utils/context/UserHookTypes.ts';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { OrganizationData } from '@openx/types/organization.ts';
import { useTranslation } from 'react-i18next';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext.tsx';
import { useUserCreateModal } from '../hooks/useUserCreateModal.tsx';

export type UsersWithAccessProps = {
  handleCreateUser: ReturnType<UseUserCreate>['createUser'];
  handleDeleteUser: ({ id, name }: { id: string; name: string }) => void;
  organization: OrganizationData;
};

export const UsersWithAccess = ({ organization, handleDeleteUser, handleCreateUser }: UsersWithAccessProps) => {
  const { t } = useTranslation();
  const { useOrganizationUsersFetch } = useOrganizationContext();
  const { users, hasMore, loading, showMore } = useOrganizationUsersFetch(organization.id);

  const openCreateUserModal = useUserCreateModal({
    handleCreateUser,
    organizationId: organization.id,
    organizationParentAccountId: organization.parent_account?.id,
  });

  const handleOpenCreateUserModal = () => openCreateUserModal();

  if (loading) return <Loader />;

  return (
    <FormSection
      divider
      title={t('Users With Access')}
      rightColumnSize={6}
      description={
        <Button color="secondary" variant="contained" onClick={handleOpenCreateUserModal} sx={{ mt: 2 }}>
          {t('Add User')}
        </Button>
      }
    >
      {users.length > 0 ? (
        <Grid container direction="row" sx={{ mb: 1, justifyContent: 'space-between', alignItems: 'center' }}>
          <Grid size={8}>
            <Typography variant="body1Bold">{t('Name')}</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="body1Bold">{t('Role')}</Typography>
          </Grid>
        </Grid>
      ) : (
        <Typography>{t('There are no users linked to this organization')}</Typography>
      )}

      {users.map(user => (
        <Grid
          container
          key={user.id}
          data-test="user-row"
          direction="row"
          sx={{
            borderTop: '1px solid',
            borderTopColor: 'divider',
            pt: 2,
            mb: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Grid size={8}>
            <Typography variant="body1Bold">
              {user.first_name} {user.last_name}
            </Typography>
            <Typography>{user.email_address}</Typography>
          </Grid>

          <Grid size={2} spacing={2}>
            <Typography>{user.role}</Typography>
          </Grid>

          <Grid size={2}>
            <IconButton
              aria-label="Delete User"
              onClick={() => handleDeleteUser({ id: user.id, name: `${user.first_name} ${user.last_name}` })}
            >
              <DeleteOutlinedIcon color="error" />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      {hasMore && (
        <Button data-test="show-more-button" variant="text" color="secondary" onClick={showMore}>
          {t('Show more Users')}
        </Button>
      )}
    </FormSection>
  );
};
